export const UNKNOWN_TOKEN_AGE = '—'

// TON accounts cannot predate mainnet. Rejecting pre-mainnet timestamps also
// prevents Go's year-1 zero value and Unix epoch sentinels from becoming huge
// but superficially valid ages. Numeric sec/ms/us/ns payloads are rejected:
// birth_ts is an RFC3339 provenance field, not a unit-guessing API.
const TON_MAINNET_EPOCH_MS = Date.parse('2019-01-01T00:00:00Z')
const RFC3339 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/

export function canonicalTokenBirthMs(value: unknown, nowMs: number = Date.now()): number | null {
  if (typeof value !== 'string') return null
  const iso = value.trim()
  if (!RFC3339.test(iso)) return null
  const parsed = Date.parse(iso)
  if (
    !Number.isFinite(parsed) ||
    !Number.isFinite(nowMs) ||
    parsed < TON_MAINNET_EPOCH_MS ||
    parsed > nowMs
  ) {
    return null
  }
  return parsed
}

export interface TokenBirthFields {
  birth_ts?: string
  discovered_at?: string
}

/** Birth is immutable provenance. Every REST/WS merge therefore keeps the
 * earliest valid chain timestamp and mirrors it to the deprecated alias. */
export function earliestValidTokenBirth(
  values: readonly unknown[],
  nowMs: number = Date.now()
): string | undefined {
  let earliest: string | undefined
  let earliestMs = Number.POSITIVE_INFINITY
  for (const value of values) {
    const parsed = canonicalTokenBirthMs(value, nowMs)
    if (parsed == null || parsed >= earliestMs) continue
    earliest = (value as string).trim()
    earliestMs = parsed
  }
  return earliest
}

export function rememberEarliestTokenBirth(
  births: Map<string, string>,
  key: string,
  value: unknown
): string | undefined {
  if (!key) return undefined
  const birthTS = earliestValidTokenBirth([births.get(key), value])
  if (!birthTS) return undefined
  births.set(key, birthTS)
  return birthTS
}

export function mergeTokenBirth<T extends TokenBirthFields>(
  base: T,
  ...sources: Array<TokenBirthFields | null | undefined>
): T {
  const birthTS = earliestValidTokenBirth(
    [base.birth_ts, base.discovered_at].concat(
      sources.flatMap((source) => [source?.birth_ts, source?.discovered_at])
    )
  )
  if (!birthTS || (base.birth_ts === birthTS && base.discovered_at === birthTS)) return base
  return { ...base, birth_ts: birthTS, discovered_at: birthTS }
}

export function formatTokenAge(value: unknown, nowMs: number): string {
  const birthMs = canonicalTokenBirthMs(value, nowMs)
  if (birthMs == null || !Number.isFinite(nowMs)) return UNKNOWN_TOKEN_AGE
  const secs = (nowMs - birthMs) / 1000
  if (secs < 60) return `${Math.floor(secs)}s`
  const mins = secs / 60
  if (mins < 60) return `${Math.floor(mins)}m`
  const hours = mins / 60
  if (hours < 24) return `${Math.floor(hours)}h`
  return `${Math.floor(hours / 24)}d`
}
